import { useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Paper, Typography } from "@mui/material";
import { useLanguage } from "./../../../contexts/LanguageContext";
import { OfferApplicantStatus } from "./../../../types/OfferApplicantStatus.interface";
import translations from "./OfferResponse.translations.json";

type SupportedLanguage = keyof typeof translations;

interface OfferResponseProps {
    status?: string;
    loading?: boolean;
    onAccept: () => void;
    onReject: () => void;
}

const OfferResponse = ({ status, loading = false, onAccept, onReject }: OfferResponseProps) => {
    const { language } = useLanguage();
    const content = translations[language as SupportedLanguage] || translations.en;
        const [pendingAction, setPendingAction] = useState<"accept" | "reject" | null>(null);

        const closeDialog = () => setPendingAction(null);

        const handleConfirm = () => {
            if (pendingAction === "accept") onAccept();
            else if (pendingAction === "reject") onReject();
            closeDialog();
        };

        if (status !== OfferApplicantStatus.OFFER_SENT) {
        return null;
    }

    return (
        <Paper sx={{ p: 3, mb: 2, bgcolor: "#161b22", border: "1px solid #30363d" }}>
            <Typography variant="h6" sx={{ color: "#f8fafc", fontWeight: 600, mb: 1 }}>
                {content.title}
            </Typography>
            <Typography variant="body2" sx={{ color: "#cbd5e1", mb: 2 }}>
                {content.description}
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button variant="contained" color="success" disabled={loading} onClick={() => setPendingAction("accept")}>
                    {content.accept}
                </Button>
                <Button variant="outlined" color="error" disabled={loading} onClick={() => setPendingAction("reject")}>
                    {content.reject}
                </Button>
            </Box>
            <Dialog open={pendingAction !== null} onClose={closeDialog}>
                <DialogTitle>{content.confirm.title}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {pendingAction === "accept" ? content.confirm.acceptMessage : content.confirm.rejectMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} disabled={loading}>{content.confirm.cancel}</Button>
                    <Button
                        variant="contained"
                        color={pendingAction === "accept" ? "success" : "error"}
                        disabled={loading}
                        onClick={handleConfirm}
                    >
                        {content.confirm.confirm}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default OfferResponse;
